import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'What is a special dividend?',
    a: 'A special (or "extra") dividend is a ONE-TIME payout outside the regular dividend schedule. The most famous example: Costco paid a $15 per share special dividend in December 2024 on top of its $1.16 regular quarterly dividend. Companies issue specials when they have excess cash, after a major asset sale, or to return capital without committing to a permanent dividend raise.',
  },
  {
    q: 'Why do specials mess up CAGR math?',
    a: 'Dividend CAGR (Compound Annual Growth Rate) is calculated from the dividend in year N vs year N-3 or N-5. A $15 special in 2024 inflates the 2024 "dividend per share" total to about $19.60 instead of the regular ~$4.50, then the next year drops back to ~$4.80. Treated naively, the math says "your dividend just fell 75%!" — but that\'s only because of the one-time payout. The streak isn\'t broken, the company didn\'t cut.',
  },
  {
    q: 'How does DiviDrip detect these distortions?',
    a: 'On the Stock Modal\'s Streak Risk Chart, DiviDrip looks at each prior year\'s dividend total and compares it to the 3-year median. If a year is MORE than 25% above that median, a "special-dividend distortion" callout appears with a yellow warning icon. The chart explains: "Your 3-year CAGR includes a one-time special in [year] — the underlying trend is closer to X% than the displayed Y%."',
  },
  {
    q: 'What stocks pay specials regularly?',
    a: 'A small but consistent group. Costco has paid 5 specials over the last 20 years (2004, 2012, 2017, 2020, 2024). EQT Corp paid one in 2015. Mastercard, Visa, and other cash-rich names occasionally consider them but most prefer buybacks. The pattern matters more than the names — once you know to look for it, the Streak Risk Chart catches the math distortion automatically.',
  },
];

export default function LearnSpecialDividendsCagr() {
  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
  };
  return (
    <LearnPage
      title="Special Dividends and CAGR Math — Why DiviDrip Flags Outlier Years | DiviDrip"
      description="A one-time Costco-style $15 special pollutes your 3-year and 5-year dividend CAGR for years afterward. How DiviDrip's Streak Risk Chart automatically detects special-dividend distortion and shows the underlying trend."
      canonical="https://twylightcrow.com/learn/special-dividends-in-cagr-math"
      guidePath="/learn/special-dividends-in-cagr-math"
      sectionLabel="Vehicles & Structures"
      sectionAnchor="vehicles"
      kicker="Learn · CAGR distortion"
      h1="Special Dividends and CAGR Math — Why DiviDrip Flags Outlier Years"
      datePublished="2026-05-24"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/yield-on-cost', label: 'Yield on cost — your real long-term yield' },
        { to: '/learn/dividend-triangle', label: 'Reading the Dividend Triangle score' },
        { to: '/learn/five-numbers-to-check', label: 'The 5 numbers I check before buying' },
        { to: '/learn/dividend-traps', label: 'Dividend trap stocks — spotting yields that lie' },
        { to: '/learn/payout-ratio', label: 'The payout ratio guide' },
      ]}
    >
      <p>
        On December 11, 2024, Costco paid a <strong>$15.00 per share</strong>{' '}
        special dividend. Anyone with 100 shares received $1,500 — on top of
        their regular quarterly $1.16 payment that quarter. Beautiful surprise.
      </p>
      <p>
        It also wrecked their dividend CAGR for the next five years.
      </p>

      <h2>The math problem</h2>
      <p>
        Dividend CAGR — your 3-year or 5-year dividend growth rate — is one of
        the cleanest signals of a healthy dividend payer. The formula:
      </p>
      <p>
        <code>CAGR = (Final / Initial)^(1/Years) − 1</code>
      </p>
      <p>For Costco WITHOUT the 2024 special:</p>
      <ul>
        <li>2021 total dividend: $3.16 per share</li>
        <li>2024 total dividend: $4.64 per share</li>
        <li>3-yr CAGR: <strong>13.7%</strong> — strong growth, totally accurate.</li>
      </ul>
      <p>For Costco WITH the 2024 special:</p>
      <ul>
        <li>2021 total dividend: $3.16</li>
        <li>2024 total dividend: $4.64 regular + $15.00 special = <strong>$19.64</strong></li>
        <li>3-yr CAGR (raw): <strong>83.9%</strong> — meaningless</li>
        <li>2025 total (no special): drops to ~$5.00. The chart now shows a 75% &quot;cut&quot;.</li>
      </ul>
      <p>
        Neither number tells the truth. The 13.7% is the real growth rate. The
        special was a one-time gift, not the start of a trend, and the 2025
        &quot;drop&quot; isn&apos;t a cut at all. Anyone reading the raw chart
        without knowing about the special draws the wrong conclusion.
      </p>

      <h2>How DiviDrip catches it</h2>
      <p>
        Open any ticker on DiviDrip and click through to the Stock Modal. On
        the Dividend Info tab, scroll down to the <strong>Streak Risk
        Chart</strong>. This visualization shows the dividend per share
        year-over-year, with a few smart filters running in the background.
      </p>
      <p>The detection rule is simple:</p>
      <ol>
        <li>Compute the 3-year median dividend (a robust center, not skewed by outliers).</li>
        <li>For each prior year, check whether the actual dividend was <strong>more than 25% above that median</strong>.</li>
        <li>If yes, that year is flagged as a likely SPECIAL DISTORTION.</li>
        <li>The chart shows a yellow callout: &quot;Your 3-year CAGR includes a one-time special in [year] — the underlying trend is closer to X% than the displayed Y%.&quot;</li>
      </ol>
      <p>
        The same logic catches the reverse situation: if a year is more than 25%
        BELOW the median, that gets flagged as &quot;possible missed payment&quot;
        or &quot;data anomaly&quot; — common when a quarterly payment is delayed
        across a calendar-year boundary.
      </p>

      <h2>The 5 most famous specials of the last decade</h2>
      <table className="learn-table">
        <thead>
          <tr><th>Year</th><th>Stock</th><th>Special amount</th><th>Why</th></tr>
        </thead>
        <tbody>
          <tr><td>2024</td><td>Costco (COST)</td><td>$15.00</td><td>Excess cash; 5th special since 2004</td></tr>
          <tr><td>2020</td><td>Costco (COST)</td><td>$10.00</td><td>Pre-COVID cash reserves</td></tr>
          <tr><td>2017</td><td>Costco (COST)</td><td>$7.00</td><td>Tax-reform anticipation</td></tr>
          <tr><td>2015</td><td>EQT Corp (EQT)</td><td>$1.00</td><td>Asset sale proceeds</td></tr>
          <tr><td>2012</td><td>Costco (COST)</td><td>$7.00</td><td>Fiscal-cliff dividend-tax planning</td></tr>
        </tbody>
      </table>
      <p>
        Costco is the textbook serial special payer. Their pattern is
        remarkable — once every 3-7 years, they declare a big one. If you own
        COST and your 3-yr CAGR looks suspicious, the Streak Risk Chart
        callout is almost certainly catching this pattern.
      </p>

      <h2>What this means for your investment decision</h2>
      <ul>
        <li>
          <strong>Don&apos;t treat a year-with-special as the new baseline.</strong>{' '}
          The dividend &quot;dropped 75%&quot; in 2025 is mechanical, not a real cut.
        </li>
        <li>
          <strong>Don&apos;t compute yield-on-cost using the special-inflated
          year.</strong> Your YoC would look magical for one year and then
          deflate. Use the regular dividend rate only.
        </li>
        <li>
          <strong>Watch for the cadence.</strong> Companies that pay specials
          every 3-5 years effectively boost shareholder returns by a few extra
          percentage points without committing to a permanent raise. That&apos;s
          a feature, not a bug — just don&apos;t mis-read the chart.
        </li>
        <li>
          <strong>Trust the Streak Risk Chart callout.</strong> When you see the
          yellow distortion warning, the &quot;underlying trend&quot; number it
          shows is the one to use — not the raw CAGR display.
        </li>
      </ul>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Try it</h2>
      <p>
        Open <Link to="/dashboard">DiviDrip</Link> and search COST. On the
        Stock Modal Dividend Info tab, scroll to the Streak Risk Chart. You&apos;ll
        see the yellow distortion callout for the 2024 special, and the chart
        explains exactly what the underlying trend is — the way every
        dividend-CAGR display should work but rarely does.
      </p>
    </LearnPage>
  );
}
